import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/shared/auth/AuthContext'
import { useAlerts } from '@/shared/api/queries'
import { useTheme } from '@/shared/hooks/useTheme'
import { initials, ROLE_LABELS } from '@/shared/format'
import { isSerious } from '@/shared/format/tones'
import { Icon } from '@/shared/ui/Icon'
import { IconButton } from '@/shared/ui/primitives'
import { titleFor } from '../navigation'

export function Topbar({ onMenu, onBell }: { onMenu: () => void; onBell: () => void }) {
  const { user, logout, hasModule } = useAuth()
  const { toggle } = useTheme()
  const alerts = useAlerts(!!user && hasModule('risk'))
  const { pathname } = useLocation()
  const nav = useNavigate()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  if (!user) return null
  const { title, eyebrow } = titleFor(pathname)
  const unread = alerts.data?.data.filter((a) => !a.read_at && isSerious(a.severity)).length ?? 0

  return (
    <header className="topbar">
      <IconButton label="Afficher le menu" icon="menu" className="topbar__menu" onClick={onMenu} />
      <div className="topbar__crumb">
        <p className="crumb__over">{eyebrow}</p>
        <p className="crumb__title">{title}</p>
      </div>
      <div className="topbar__tools" ref={ref}>
        <div className="scope">
          <span className="pill pill--mute pill--plain">
            {user.stations?.length
              ? user.stations.map((s) => s.code).join(' · ')
              : 'Tout le réseau'}
          </span>
        </div>
        <IconButton label="Changer de thème" icon="moon" onClick={toggle} />
        <button
          type="button"
          className="icon-btn icon-btn--bell"
          aria-label={`Alertes (${unread} non lues)`}
          onClick={onBell}
        >
          <Icon name="bell" />
          {unread > 0 && <span className="badge-dot">{unread}</span>}
        </button>
        <button
          type="button"
          className="who"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          <span className="who__avatar">{initials(user.name)}</span>
          <span className="who__meta">
            <span className="who__name">{user.name}</span>
            <span className="who__role">{ROLE_LABELS[user.role]}</span>
          </span>
          <Icon name="chev" className="who__chev" />
        </button>
        {open && (
          <div className="who__menu">
            <p>Session</p>
            <div style={{ padding: '4px 10px 8px', fontSize: 12.5 }}>
              <b>{user.email}</b>
              <br />
              <span className="muted">{user.employee_code ?? 'sans matricule'}</span>
            </div>
            <div style={{ borderTop: '1px solid var(--border-soft)', paddingTop: 6 }}>
              <button
                type="button"
                className="role-pick"
                style={{ border: 0, padding: '8px 10px' }}
                onClick={() => {
                  setOpen(false)
                  void logout().then(() => nav('/login'))
                }}
              >
                <span
                  className="role-pick__ini"
                  style={{
                    width: 26,
                    height: 26,
                    background: 'var(--sunken)',
                    color: 'var(--ink-2)',
                  }}
                >
                  ↩
                </span>
                <span>
                  <span className="role-pick__n">Se déconnecter</span>
                </span>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
